<script lang="ts" setup>
import { getDefaultSubaccountId } from '@injectivelabs/sdk-ts'
import { formatMeta } from '@/app/utils/formatter'
import {
  MetaType,
  StatusKey,
  InjectionKey,
  NetworkErrorType,
  ContractSubPage
} from '@/types'

definePageMeta({
  middleware: ['contract-search']
})
useHead(formatMeta(MetaType.Contract))

const route = useRoute()
const appStore = useAppStore()
const contractStore = useContractStore()
const accountStore = useAccountStore()
const { $onError } = useNuxtApp()
const { statusMap } = useStatusMap()

const routeParamsContract = route.params.contract as string
const defaultSubaccountId = getDefaultSubaccountId(routeParamsContract)

const subaccountId = ref(defaultSubaccountId)
const isBalanceBreakdownEnabled = ref(false)

const contractSymbol = computed(() => {
  const contract = contractStore.contract

  if (!contract || !contract.initMessage || !contract.initMessage.symbol) {
    return
  }

  return contract.initMessage.symbol
})

const isBalancesPage = computed(
  () => (route.name as string) === ContractSubPage.BalancePage
)

const isSubaccountSelectorVisible = computed(
  () =>
    Object.keys(accountStore.accountBalancesMap).length > 1 &&
    !!subaccountId.value
)

const isNetworkError = computed(() =>
  appStore.networkErrorTypes.includes(NetworkErrorType.ContractDetailsPage)
)

const hasBalances = computed(
  () =>
    accountStore.accountBalancesMap[getDefaultSubaccountId(routeParamsContract)]
      ?.length || 0
)

onMounted(() => {
  statusMap[StatusKey.AccountPositionsInitStatus].setLoading()

  contractStore
    .fetchContract(routeParamsContract)
    .then(async () => {
      await Promise.all([
        accountStore.fetchPortfolio(routeParamsContract),
        contractStore.fetchContract(routeParamsContract),
        contractStore.fetchCW20AccountBalancesWithToken({
          contractAddress: routeParamsContract,
          limit: 10
        }),
        contractStore.fetchContractTransactions({
          contractAddress: routeParamsContract,
          limit: 20,
          updateTotalCount: true
        }),
        accountStore.fetchPortfolio(routeParamsContract),
        accountStore.fetchCW20BalancesNoThrow(routeParamsContract)
      ])
    })
    .catch($onError)
    .finally(() => {
      statusMap[StatusKey.AccountPositionsInitStatus].setIdle()
    })
})

onBeforeUnmount(() => {
  accountStore.reset()
})

onUnmounted(() => {
  contractStore.reset()
})

provide(InjectionKey.SubaccountIdKey, subaccountId)
provide(InjectionKey.IsBalanceBreakdownEnabledKey, isBalanceBreakdownEnabled)
</script>

<template>
  <AppHocLoading
    v-bind="{
      isLoading:
        statusMap[StatusKey.AccountPositionsInitStatus].isLoading() ||
        statusMap[StatusKey.AccountPositionsInitStatus].isLoading()
    }"
  >
    <AppCard class="relative min-h-xs">
      <AppNetworkErrorWrapper
        :network-error-type="NetworkErrorType.ContractDetailsPage"
      >
        <PartialsContractDetailsHeader
          v-if="contractStore.contract"
          class="fade-in-animation"
          :contract="contractStore.contract"
          :symbol="contractSymbol"
        />
      </AppNetworkErrorWrapper>
    </AppCard>

    <div v-if="!isNetworkError" class="mt-6 relative">
      <div class="flex justify-between items-center max-lg:flex-wrap gap-6">
        <div class="w-full">
          <div class="flex flex-wrap items-center gap-6">
            <PartialsCommonHeaderLinkTab
              is-index
              v-bind="{
                to: {
                  name: ContractSubPage.IndexPage,
                  params: route.params
                }
              }"
            >
              {{ $t('common.transactions') }}
            </PartialsCommonHeaderLinkTab>

            <PartialsCommonHeaderLinkTab
              v-if="hasBalances"
              v-bind="{
                to: {
                  name: ContractSubPage.BalancePage,
                  params: route.params
                }
              }"
            >
              {{ $t('account.balances') }}
            </PartialsCommonHeaderLinkTab>

            <PartialsCommonHeaderLinkTab
              v-if="accountStore.cw20Balances.length > 0"
              v-bind="{
                to: {
                  name: ContractSubPage.CW20BalancePage,
                  params: route.params
                }
              }"
            >
              {{ $t('account.cw20Balances') }}
            </PartialsCommonHeaderLinkTab>

            <PartialsCommonHeaderLinkTab
              v-bind="{
                to: {
                  name: ContractSubPage.InitMsgPage,
                  params: route.params
                }
              }"
            >
              {{ $t('smartContracts.contract.initMsg') }}
            </PartialsCommonHeaderLinkTab>

            <PartialsCommonHeaderLinkTab
              v-if="contractStore.cw20AccountBalancesWithToken.length"
              v-bind="{
                to: {
                  name: ContractSubPage.TokenHoldersPage,
                  params: route.params
                }
              }"
            >
              {{ $t('smartContracts.contract.tokenHolders') }}
            </PartialsCommonHeaderLinkTab>

            <PartialsCommonHeaderLinkTab
              v-bind="{
                to: {
                  name: ContractSubPage.ActionPage,
                  params: route.params
                }
              }"
            >
              {{ $t('smartContracts.contract.queryAndExecute.title') }}
            </PartialsCommonHeaderLinkTab>
          </div>

          <div
            v-if="isBalancesPage"
            class="flex items-center flex-wrap gap-6 md:justify-end mt-6"
          >
            <AppButtonCheckbox
              v-model="isBalanceBreakdownEnabled"
              v-bind="{ title: $t('account.isBalanceBreakdownEnabled') }"
            />

            <PartialsAccountSubaccountSelector
              v-if="isBalancesPage && isSubaccountSelectorVisible"
              v-model:subaccount-id="subaccountId"
            />
          </div>
        </div>

        <div id="contract-details-page-selector" />
      </div>

      <div class="relative min-h-xs lg:mt-6">
        <NuxtPage
          v-bind="{
            contract: contractStore.contract,
            contractAddress: routeParamsContract
          }"
          :transition="{
            name: 'fade',
            mode: 'out-in'
          }"
        />
      </div>
    </div>
  </AppHocLoading>
</template>
